import type { IUnifiedOrderBlotterEntry } from 'apiConnector/blotters';
import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

export type SecondaryMarketOrderBlotterAction =
  | SecondaryMarketOrderBlotterItemsReloaded
  | SecondaryMarketOrderBlotterColumnDefinitionReceived
  | SecondaryMarketOrderBlotterItemsReceived
  | SecondaryMarketOrderBlotterItemsRequested
  | SecondaryMarketOrderBlotterItemsFetchingFailed
  | SecondaryMarketOrderBlotterColumnStateChanged
  | SecondaryMarketOrderBlotterPreferenceStatePersisted
  | SecondaryMarketOrderBlotterPreferenceStateLoaded
  | SecondaryMarketOrderBlotterPreferenceStateLoadingFailed
  | SecondaryMarketOrderBlotterPreferenceStateGet
  | SecondaryMarketOrdersBlotterDisplayedItemsCntChanged
  | SecondaryMarketOrderBlotterFiltersLoadingFailed
  | SecondaryMarketOrderBlotterFilterByOrderId
  | SecondaryMarketOrderBlotterSetColumnFilter;

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterColumnDefinitionReceived {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_DEFINITION_RECEIVED';
  columnDefinition: IBlotterColumnDefinition<IUnifiedOrderBlotterEntry>[];
}
export function secondaryMarketOrderBlotterColumnDefinitionReceived(
  columnDefinition: IBlotterColumnDefinition<IUnifiedOrderBlotterEntry>[],
): SecondaryMarketOrderBlotterColumnDefinitionReceived {
  return {
    type: 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_DEFINITION_RECEIVED',
    columnDefinition,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterItemsReceived {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RECEIVED';
  items: readonly any[];
}
export function secondaryMarketOrderBlotterItemsReceived(
  items: readonly IUnifiedOrderBlotterEntry[],
): SecondaryMarketOrderBlotterItemsReceived {
  return {
    type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RECEIVED',
    items,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterItemsRequested {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_REQUESTED';
}
export function secondaryMarketOrderBlotterItemsRequested(): SecondaryMarketOrderBlotterItemsRequested {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_REQUESTED' };
}

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterItemsFetchingFailed {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_FETCHING_FAILED';
  error: string;
}
export function secondaryMarketOrderBlotterItemsFetchingFailed(
  error: string,
): SecondaryMarketOrderBlotterItemsFetchingFailed {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_FETCHING_FAILED', error };
}

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterColumnStateChanged {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_STATE_CHANGED';
  columnState: readonly any[];
}
export function secondaryMarketOrderBlotterColumnStateChanged(
  columnState: readonly any[],
): SecondaryMarketOrderBlotterColumnStateChanged {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_STATE_CHANGED', columnState };
}
export function secondaryMarketOrdersBlotterDisplayedItemsCntChanged(cnt: number) {
  return {
    type: 'SECONDARY_MARKET_ORDER_BLOTTER_DISPLAYED_ITEMS_CNT_CHANGED' as const,
    cnt,
  };
}
export type SecondaryMarketOrdersBlotterDisplayedItemsCntChanged = ReturnType<
  typeof secondaryMarketOrdersBlotterDisplayedItemsCntChanged
>;

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterPreferenceStatePersisted {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_PERSISTED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketOrderBlotterPreferenceStatePersisted(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketOrderBlotterPreferenceStatePersisted {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_PERSISTED', blotterPreferenceState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterPreferenceStateLoaded {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketOrderBlotterPreferenceStateLoaded(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketOrderBlotterPreferenceStateLoaded {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED', blotterPreferenceState };
}
//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterPreferenceStateGet {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_GET';
}
export function secondaryMarketOrderBlotterPreferenceStateGet(): SecondaryMarketOrderBlotterPreferenceStateGet {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_GET' };
}

export function secondaryMarketOrderBlotterPreferenceStateLoadingFailed() {
  return {
    type: 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADING_FAILED' as const,
  };
}
export type SecondaryMarketOrderBlotterPreferenceStateLoadingFailed = ReturnType<
  typeof secondaryMarketOrderBlotterPreferenceStateLoadingFailed
>;

export function secondaryMarketOrderBlotterFiltersLoadingFailed() {
  return {
    type: 'SECONDARY_MARKET_ORDER_BLOTTER_LOADING_FILTERS_FAILED' as const,
  };
}
export type SecondaryMarketOrderBlotterFiltersLoadingFailed = ReturnType<
  typeof secondaryMarketOrderBlotterFiltersLoadingFailed
>;

//////////////////////////////////////////////////
export interface SecondaryMarketOrderBlotterItemsReloaded {
  type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RELOADED';
}
export function secondaryMarketOrderBlotterItemsReloaded(): SecondaryMarketOrderBlotterItemsReloaded {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RELOADED' };
}

export function secondaryMarketOrderBlotterFilterByOrderId(orderId?: string) {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_FILTER_BY_ORDER_ID' as const, orderId };
}

export type SecondaryMarketOrderBlotterFilterByOrderId = ReturnType<typeof secondaryMarketOrderBlotterFilterByOrderId>;
export function secondaryMarketOrderBlotterSetColumnFilter(columnName: string, filterType: 'multi', filterModels: any) {
  return { type: 'SECONDARY_MARKET_ORDER_BLOTTER_SET_COLUMN_FILTER' as const, columnName, filterType, filterModels };
}

export type SecondaryMarketOrderBlotterSetColumnFilter = ReturnType<typeof secondaryMarketOrderBlotterSetColumnFilter>;
